import {
  SidebarReorderableList,
  SidebarScrollableContainer,
} from '@affine/core/modules/app-sidebar/views';
import clsx from 'clsx';
import type { ComponentProps, ReactNode, Ref, RefObject } from 'react';

import {
  NavigationPanelTreeSelectionProvider,
  NavigationPanelTreeSelectionToolbar,
} from '../../desktop/components/navigation-panel/tree';
import {
  NavigationPanelRevealRenameCompletionContext,
  type NavigationPanelRevealRenameFinishReason,
} from '../../desktop/components/navigation-panel/tree/rename-intent';
import {
  SidebarExpandableGroup,
  SidebarExpandableSection,
} from '../sidebar-expandable-section';
import { WorkspaceAppSidebarAreas } from '../workspace-app-sidebar';
import { terminalAreaGroup } from '../workspace-app-sidebar/index.css';
import {
  organizeSections,
  resizableAreaOrganizeSections,
  sidebarBottomSection,
  sidebarExpandableList,
  sidebarExpandableScroll,
  sidebarMiddleScroll,
  sidebarOrganization,
  sidebarWorkspaceExpandableList,
  workspaceAreaSection,
  workspaceSharedAreaGroup,
} from './index.css';
import { NavigationOverlayPane } from './navigation-overlay-pane';
import * as railStyles from './ui-redesign-rail.css';

const NAVIGATION_TREE_SNAP_TARGET_SELECTOR = [
  '[data-navigation-panel-section-header="true"]',
  '[data-navigation-panel-tree-node-trigger="true"]',
  '[data-navigation-panel-tree-placeholder="true"]',
].join(',');

type ExpandableSectionProps = Omit<
  ComponentProps<typeof SidebarExpandableSection>,
  | 'action'
  | 'children'
  | 'className'
  | 'collapsedSummary'
  | 'contentClassName'
  | 'contentPadding'
  | 'headerRef'
  | 'testId'
  | 'title'
>;

type ScrollProps = Omit<
  ComponentProps<typeof SidebarScrollableContainer>,
  'children' | 'className' | 'contentPadding'
>;

interface DocumentsSidebarAreaProps {
  action?: ReactNode;
  children: ReactNode;
  groupClassName?: string;
  listRef?: RefObject<HTMLDivElement | null>;
  scrollProps: ScrollProps;
  sectionClassName?: string;
  sectionContentClassName?: string;
  sectionHeaderRef?: ComponentProps<
    typeof SidebarExpandableSection
  >['headerRef'];
  sectionProps: ExpandableSectionProps;
  title: ReactNode;
}

interface DocumentsSidebarWorkspaceAreaProps extends DocumentsSidebarAreaProps {
  collapsedSummary: ReactNode;
  fillLayout: ComponentProps<typeof SidebarExpandableGroup>['fillLayout'];
}

interface DocumentsSidebarTeamAreaProps extends DocumentsSidebarAreaProps {
  headerHeight: number;
  groupRef: Ref<HTMLDivElement>;
}

interface DocumentsSidebarAppsAreaProps {
  children: ReactNode;
  scrollProps: ScrollProps;
  sectionProps: ExpandableSectionProps;
  title: ReactNode;
}

export interface DocumentsSidebarContentProps {
  apps?: DocumentsSidebarAppsAreaProps | undefined;
  closeLabel: string;
  footer: ReactNode;
  mobileOpen: boolean;
  navigationRevealDocId?: string | undefined;
  navigationRevealRename?: boolean | undefined;
  navigationRevealRequestId?: number | undefined;
  onNavigationRevealRenameFinish: (
    requestId: number,
    reason: NavigationPanelRevealRenameFinishReason
  ) => void;
  onClose: () => void;
  open: boolean;
  personal: DocumentsSidebarAreaProps & {
    fillLayout: ComponentProps<typeof SidebarExpandableGroup>['fillLayout'];
  };
  rootRef: Ref<HTMLDivElement>;
  teamAreas: DocumentsSidebarTeamAreaProps;
  title: ReactNode;
  twoPane: boolean;
  workspace: DocumentsSidebarWorkspaceAreaProps;
}

export function DocumentsSidebarContent({
  apps,
  closeLabel,
  footer,
  mobileOpen,
  navigationRevealDocId,
  navigationRevealRename,
  navigationRevealRequestId,
  onNavigationRevealRenameFinish,
  onClose,
  open,
  personal,
  rootRef,
  teamAreas,
  title,
  twoPane,
  workspace,
}: DocumentsSidebarContentProps) {
  return (
    <NavigationPanelRevealRenameCompletionContext.Provider
      value={onNavigationRevealRenameFinish}
    >
      <NavigationOverlayPane
        closeLabel={closeLabel}
        contentProps={{
          ref: rootRef,
          'data-navigation-reveal-surface': 'true',
          'data-navigation-reveal-doc-id': navigationRevealDocId,
          'data-navigation-reveal-request-id': navigationRevealRequestId,
          'data-navigation-reveal-rename': navigationRevealRename
            ? 'true'
            : undefined,
        }}
        footer={footer}
        mobileOpen={mobileOpen}
        onClose={onClose}
        open={open}
        testId="global-navigation-docs-panel"
        title={title}
        twoPane={twoPane}
      >
        <SidebarExpandableGroup
          className={workspace.groupClassName}
          expanded={workspace.sectionProps.expanded}
          fillLayout={workspace.fillLayout}
          placement="top"
        >
          <SidebarExpandableSection
            className={workspace.sectionClassName}
            collapsedSummary={workspace.collapsedSummary}
            contentPadding="none"
            contentClassName={workspace.sectionContentClassName}
            {...workspace.sectionProps}
            testId="sidebar-workspace"
            title={workspace.title}
          >
            <SidebarScrollableContainer
              className={sidebarExpandableScroll}
              contentPadding="none"
              {...workspace.scrollProps}
            >
              <SidebarReorderableList
                ref={workspace.listRef}
                className={clsx(
                  sidebarExpandableList,
                  sidebarWorkspaceExpandableList
                )}
                scope="root-sidebar:workspace-navigation"
              >
                {workspace.children}
              </SidebarReorderableList>
            </SidebarScrollableContainer>
          </SidebarExpandableSection>
        </SidebarExpandableGroup>

        <WorkspaceAppSidebarAreas
          persistenceKey="workspace"
          personal={{
            content: (
              <SidebarScrollableContainer
                className={sidebarExpandableScroll}
                contentPadding="none"
                {...personal.scrollProps}
              >
                <div
                  data-testid="sidebar-personal-organization"
                  className={sidebarOrganization}
                >
                  <NavigationPanelTreeSelectionProvider>
                    <div
                      ref={personal.listRef}
                      data-testid="sidebar-personal-organize"
                      className={resizableAreaOrganizeSections}
                    >
                      {personal.children}
                    </div>
                    <NavigationPanelTreeSelectionToolbar />
                  </NavigationPanelTreeSelectionProvider>
                </div>
              </SidebarScrollableContainer>
            ),
            defaultVisibleItems: 2,
            groupProps: {
              className: personal.groupClassName,
              expanded: personal.sectionProps.expanded,
              fillLayout: personal.fillLayout,
              placement: 'top',
            },
            measurementRef: personal.listRef,
            // An expanded subtree needs room for its header and first row
            // (including its empty state), even with an older saved height.
            minimumVisibleItems: 2,
            sectionProps: {
              action: personal.action,
              className: personal.sectionClassName,
              contentClassName: personal.sectionContentClassName,
              headerRef: personal.sectionHeaderRef,
              ...personal.sectionProps,
              testId: 'sidebar-personal',
              title: personal.title,
            },
            snapTargetSelector: NAVIGATION_TREE_SNAP_TARGET_SELECTOR,
          }}
          teamAreas={{
            content: (
              <SidebarScrollableContainer
                className={sidebarMiddleScroll}
                contentPadding="none"
                {...teamAreas.scrollProps}
              >
                <div
                  data-testid="sidebar-organization"
                  className={sidebarOrganization}
                >
                  <NavigationPanelTreeSelectionProvider>
                    <div
                      ref={teamAreas.listRef}
                      data-testid="sidebar-organize"
                      className={resizableAreaOrganizeSections}
                    >
                      {teamAreas.children}
                    </div>
                    <NavigationPanelTreeSelectionToolbar />
                  </NavigationPanelTreeSelectionProvider>
                </div>
              </SidebarScrollableContainer>
            ),
            groupProps: {
              ref: teamAreas.groupRef,
              className: teamAreas.groupClassName,
              expanded: teamAreas.sectionProps.expanded,
              fillLayout: 'single-top-fill',
              headerHeight: teamAreas.headerHeight,
              placement: 'top',
            },
            sectionProps: {
              action: teamAreas.action,
              className: teamAreas.sectionClassName,
              contentClassName: teamAreas.sectionContentClassName,
              headerRef: teamAreas.sectionHeaderRef,
              ...teamAreas.sectionProps,
              testId: 'sidebar-team-areas',
              title: teamAreas.title,
            },
          }}
        />

        {apps ? (
          <SidebarExpandableGroup
            className={clsx(
              sidebarBottomSection,
              terminalAreaGroup,
              railStyles.docsPanelAppsGroup
            )}
            expanded={apps.sectionProps.expanded}
            placement={apps.sectionProps.placement}
          >
            <SidebarExpandableSection
              contentPadding="none"
              {...apps.sectionProps}
              testId="sidebar-apps"
              title={apps.title}
            >
              <SidebarScrollableContainer
                className={sidebarExpandableScroll}
                contentPadding="none"
                {...apps.scrollProps}
              >
                <div
                  data-testid="sidebar-apps-organization"
                  className={sidebarOrganization}
                >
                  <NavigationPanelTreeSelectionProvider>
                    <div
                      data-testid="sidebar-apps-organize"
                      className={organizeSections}
                    >
                      <SidebarReorderableList
                        className={workspaceSharedAreaGroup}
                        scope="root-sidebar:app-areas"
                      >
                        <div className={workspaceAreaSection}>
                          {apps.children}
                        </div>
                      </SidebarReorderableList>
                    </div>
                    <NavigationPanelTreeSelectionToolbar />
                  </NavigationPanelTreeSelectionProvider>
                </div>
              </SidebarScrollableContainer>
            </SidebarExpandableSection>
          </SidebarExpandableGroup>
        ) : null}
      </NavigationOverlayPane>
    </NavigationPanelRevealRenameCompletionContext.Provider>
  );
}
